import cn from 'classnames';
import { memo, useEffect, useRef, useState } from 'react';

import { formatOrderbookNumber } from '../lib/calculatePercentages';
import { OrderbookRowProps } from '../model/types';

/**
 * Optimized orderbook row component with smooth animations
 *
 * Features:
 * - React.memo with custom comparison to prevent unnecessary re-renders
 * - CSS transitions for opacity and background-color (200-300ms)
 * - Flash highlight when amount changes
 * - Stable rendering using price as React key (done in parent)
 * - Gate.io-style smooth updates
 * - Placeholder row support (dimmed skeleton for maintaining fixed depth)
 */
export const OrderbookRow = memo<OrderbookRowProps>(
  ({ entry, type, isHovered, isInHoveredRange, onClick, style }) => {
    const [isFlashing, setIsFlashing] = useState(false);
    const prevAmountRef = useRef<string>(entry.amount);
    const flashTimeoutRef = useRef<number | null>(null);

    // Detect amount changes and trigger flash animation (skip for placeholders)
    useEffect(() => {
      if (entry.isPlaceholder) return;

      if (prevAmountRef.current !== entry.amount) {
        setIsFlashing(true);

        // Clear existing timeout
        if (flashTimeoutRef.current !== null) {
          clearTimeout(flashTimeoutRef.current);
        }

        // Remove flash after 300ms
        flashTimeoutRef.current = window.setTimeout(() => {
          setIsFlashing(false);
          flashTimeoutRef.current = null;
        }, 300);

        prevAmountRef.current = entry.amount;
      }

      return () => {
        if (flashTimeoutRef.current !== null) {
          clearTimeout(flashTimeoutRef.current);
        }
      };
    }, [entry.amount, entry.isPlaceholder]);

    const priceColor = type === 'ask' ? 'text-red-500' : 'text-green-500';
    const bgColor = type === 'ask' ? 'bg-red-500/20' : 'bg-green-500/20';
    const flashColor = type === 'ask' ? 'bg-red-500/30' : 'bg-green-500/30';

    // Placeholder rows: dimmed skeleton style
    const isPlaceholder = entry.isPlaceholder === true;

    return (
      <div
        className={cn(
          'flex items-center text-xs relative',
          // Smooth transitions for all state changes
          'transition-all duration-200 ease-out',
          {
            // Placeholder rows: skeleton style with reduced opacity
            'opacity-30 cursor-default': isPlaceholder,
            'pointer-events-none': isPlaceholder,

            // Normal rows: interactive with hover states
            'cursor-pointer': !isPlaceholder,
            'bg-zinc-800/30': !isPlaceholder && isInHoveredRange,
            'bg-zinc-700/50': !isPlaceholder && isHovered,
            'hover:bg-zinc-800/50': !isPlaceholder && !isHovered && !isInHoveredRange,
            [flashColor]: !isPlaceholder && isFlashing,
          },
        )}
        style={{
          ...style,
          height: '20px',
        }}
        onClick={isPlaceholder ? undefined : onClick}
      >
        {/* Price column */}
        <div
          className={cn(
            'w-[35%] px-2 flex items-center gap-1',
            isPlaceholder ? 'text-zinc-700' : priceColor,
          )}
        >
          <span className="font-mono">{isPlaceholder ? '—' : entry.price}</span>
          {!isPlaceholder && entry.hasOrders && (
            <div className="w-1 h-1 rounded-full bg-yellow-600 flex-shrink-0 animate-pulse" />
          )}
        </div>

        {/* Amount column - with subtle opacity transition */}
        <div
          className={cn(
            'w-[33%] px-2 text-right font-mono',
            'transition-opacity duration-200 ease-out',
            isPlaceholder ? 'text-zinc-700' : 'text-zinc-300',
          )}
        >
          {isPlaceholder ? '—' : formatOrderbookNumber(entry.amount, 4)}
        </div>

        {/* Total column with percentage background */}
        <div
          className={cn(
            'w-[32%] px-2 text-right font-mono relative overflow-hidden',
            isPlaceholder ? 'text-zinc-700' : 'text-zinc-300',
          )}
        >
          {/* Animated percentage bar - smooth width transitions */}
          {!isPlaceholder && (
            <div
              className={cn(
                'absolute top-0 left-0 h-full transition-all duration-300 ease-out',
                bgColor,
              )}
              style={{ width: `${entry.percentage}%` }}
            />
          )}
          {/* Total value */}
          <span className="relative z-10">
            {isPlaceholder ? '—' : formatOrderbookNumber(entry.total, 2)}
          </span>
        </div>
      </div>
    );
  },
  // Custom comparison for memo - only re-render if these props change
  // CRITICAL: This prevents 90%+ of unnecessary re-renders
  (prevProps, nextProps) =>
    prevProps.entry.price === nextProps.entry.price &&
    prevProps.entry.amount === nextProps.entry.amount &&
    prevProps.entry.total === nextProps.entry.total &&
    prevProps.entry.percentage === nextProps.entry.percentage &&
    prevProps.entry.isPlaceholder === nextProps.entry.isPlaceholder &&
    prevProps.isHovered === nextProps.isHovered &&
    prevProps.isInHoveredRange === nextProps.isInHoveredRange,
);

OrderbookRow.displayName = 'OrderbookRow';
